import React, { useState } from 'react';
import Modal from 'react-modal';
import ImportAssetsTable from './ImportAssetsTable';

// Set the app element for accessibility (prevents screen readers from reading background content)
Modal.setAppElement('#root');

/**
 * Modal for fetching properties of every node in the Autodesk Forge Viewer.
 * Uses getBulkProperties2 in batches to avoid overloading the call.
 */
const ImportAssetsModal = ({ isOpen, onClose, viewer }) => {
    const [fetchedData, setFetchedData] = useState(null);
    const [isLoading, setIsLoading] = useState(false);
    
    // Handle modal close and reset data
    const handleClose = () => {
        setFetchedData(null);
        setIsLoading(false);
        // Re-enable body scroll
        document.body.style.overflow = '';
        onClose();
    };
    
    // State to trigger re-render on window resize for dynamic height calculation
    const [windowSize, setWindowSize] = useState({ width: window.innerWidth, height: window.innerHeight });

    // Handle window resize for dynamic height calculation
    React.useEffect(() => {
        const handleResize = () => {
            setWindowSize({ width: window.innerWidth, height: window.innerHeight });
        };

        if (isOpen) {
            window.addEventListener('resize', handleResize);
            return () => {
                window.removeEventListener('resize', handleResize);
            };
        }
    }, [isOpen]);

    // Manual scroll prevention (react-modal's default doesn't always work with complex tables)
    React.useEffect(() => {
        if (isOpen) {
            // Store original overflow value
            const originalOverflow = document.body.style.overflow;
            // Disable body scroll
            document.body.style.overflow = 'hidden';
            
            return () => {
                // Restore original overflow
                document.body.style.overflow = originalOverflow;
            };
        }
    }, [isOpen]);

    // Collect every dbId in the model via recursion
    const getAllDbIds = () => {
        const tree = viewer.model.getInstanceTree();
        const rootId = typeof viewer.model.getRootId === 'function'
            ? viewer.model.getRootId()
            : tree.getRootId();

        const ids = [];
        const recurse = (dbId) => {
            ids.push(dbId);
            tree.enumNodeChildren(dbId, recurse);
        };
        recurse(rootId);
        return ids;
    };

    // Batch-fetch properties using getBulkProperties2
    const fetchPropertiesInBatches = async (dbIds) => {
        const batchSize = 100;  // adjust as needed based on performance
        const batches = [];
        for (let i = 0; i < dbIds.length; i += batchSize) {
            batches.push(dbIds.slice(i, i + batchSize));
        }

        const allResults = [];
        for (const batch of batches) {
            const results = await new Promise((resolve, reject) => {
                // Pass an empty options object instead of null to avoid propFilter errors
                viewer.model.getBulkProperties2(
                    batch,
                    {},           // empty options to fetch all properties
                    (res) => resolve(res),
                    (err) => reject(err)
                );
            });
            allResults.push(...results);
        }
        return allResults;
    };

    // Handler to fetch and display properties in table
    const handleFetchProperties = async () => {
        if (!viewer || !viewer.model) {
            console.warn('Viewer not ready');
            return;
        }
        
        setIsLoading(true);
        try {
            const dbIds = getAllDbIds();
            console.log(`Fetching properties for ${dbIds.length} dbIds in batches...`);
            const props = await fetchPropertiesInBatches(dbIds);
            console.log('Fetched properties:', props);
            setFetchedData(props);
        } catch (err) {
            console.error('Error fetching properties:', err);
            setFetchedData(null);
        } finally {
            setIsLoading(false);
        }
    };

    // Dynamic height calculation based on content and viewport
    const calculateModalHeight = () => {
        if (!fetchedData) {
            // Small modal for initial fetch button - let it size naturally
            return 'auto';
        }
        
        // Calculate available viewport height minus margins and padding
        const viewportHeight = windowSize.height;
        const modalPadding = 80; // Account for overlay padding and modal padding
        const headerHeight = 60; // Approximate height of modal header and buttons
        const availableHeight = viewportHeight - modalPadding - headerHeight;
        
        // Minimum height for table to be useful (but not too small on mobile)
        const minHeight = Math.max(300, Math.min(400, availableHeight * 0.5));
        
        // Preferred height based on screen size
        let preferredHeight;
        if (viewportHeight < 600) {
            // Mobile/small screens - use more of the available space
            preferredHeight = availableHeight * 0.9;
        } else if (viewportHeight < 900) {
            // Medium screens - balanced approach
            preferredHeight = availableHeight * 0.8;
        } else {
            // Large screens - don't make it too tall
            preferredHeight = Math.min(availableHeight * 0.75, 700);
        }
        
        return Math.max(minHeight, Math.min(preferredHeight, availableHeight));
    };

    // Custom styles for react-modal with dynamic sizing
    const modalStyles = {
        overlay: {
            backgroundColor: 'rgba(0, 0, 0, 0.5)',
            zIndex: 1000,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            padding: '20px' // Add padding to overlay for better spacing
        },
        content: {
            position: 'relative',
            top: 'auto',
            left: 'auto',
            right: 'auto',
            bottom: 'auto',
            border: 'none',
            background: '#fff',
            borderRadius: '8px',
            padding: '20px',
            maxWidth: fetchedData ? '95vw' : '400px',
            width: fetchedData ? '90vw' : 'auto',
            height: calculateModalHeight(),
            maxHeight: 'calc(100vh - 40px)', // Ensure it never exceeds viewport
            overflow: 'hidden',
            textAlign: fetchedData ? 'left' : 'center',
            display: 'flex',
            flexDirection: 'column',
            inset: 'auto', // Override default positioning
            margin: 'auto' // Center the modal
        }
    };

    return (
        <Modal
            isOpen={isOpen}
            onRequestClose={handleClose}
            style={modalStyles}
            contentLabel="Import Assets Modal"
            shouldCloseOnOverlayClick={true}
            shouldCloseOnEsc={true}
            shouldFocusAfterRender={true}
            shouldReturnFocusAfterClose={true}
            onAfterOpen={() => {
                // Additional scroll prevention when modal opens
                document.body.style.overflow = 'hidden';
            }}
            onAfterClose={() => {
                // Ensure scroll is restored when modal closes
                document.body.style.overflow = '';
            }}
        >
            <div 
                onWheel={(e) => {
                    // Prevent wheel events from bubbling to the body
                    e.stopPropagation();
                }}
                onTouchMove={(e) => {
                    // Prevent touch scroll events from bubbling
                    e.stopPropagation();
                }}
                style={{ height: '100%', display: 'flex', flexDirection: 'column' }}
            >
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '15px' }}>
                    <h5>Import Assets</h5>
                    <button 
                        onClick={handleClose} 
                        style={{ border: 'none', background: 'none', fontSize: '20px', cursor: 'pointer' }}
                        aria-label="Close modal"
                    >
                        ×
                    </button>
                </div>
                
                {!fetchedData && (
                    <>
                        <button 
                            className="btn btn-primary" 
                            onClick={handleFetchProperties} 
                            disabled={isLoading}
                            style={{ marginBottom: '10px' }}
                        >
                            {isLoading ? 'Fetching...' : 'Fetch All Properties'}
                        </button>
                        <br />
                        <button className="btn btn-secondary" onClick={handleClose}>
                            Close
                        </button>
                    </>
                )}
                
                {fetchedData && (
                    <>
                        <div style={{ marginBottom: '15px', display: 'flex', gap: '10px', alignItems: 'center' }}>
                            <button 
                                className="btn btn-primary btn-sm" 
                                onClick={handleFetchProperties}
                                disabled={isLoading}
                            >
                                {isLoading ? 'Refreshing...' : 'Refresh Data'}
                            </button>
                            <button className="btn btn-secondary btn-sm" onClick={handleClose}>
                                Close
                            </button>
                        </div>
                        <div style={{ 
                            flex: 1, 
                            overflow: 'hidden',
                            minHeight: 0 // Important for flex child to shrink properly
                        }}>
                            <ImportAssetsTable data={fetchedData} />
                        </div>
                    </>
                )}
            </div>
        </Modal>
    );
};

export default ImportAssetsModal;
